<template>
  <div class="alarm_container">
    <div class="alarm_header">
      <p class="total_count">
        총 {{ visibleList.length }} 건
        <span v-if="unreadCount > 0" class="unread_badge">새 답변 {{ unreadCount }}</span>
      </p>
      <div class="alarm_options">
        <div class="switch_group input_switch">
          <p :class="{ alarm_text: alarmSwitch }">{{ alarmSwitch ? '알림 ON' : '알림 OFF' }}</p>
          <input role="switch" type="checkbox" v-model="alarmSwitch" />
        </div>
        <div class="read_button_group">
          <button :disabled="unreadCount === 0 || marking" @click="markAllRead">모두 읽음으로 처리</button>
          <button :disabled="readCount === 0" @click="hideRead = !hideRead">
            {{ hideRead ? '읽은 알림 보기' : '읽은 알림 숨기기' }}
          </button>
        </div>
      </div>
    </div>

    <div class="alarmh_body">
      <div class="alarm_loading" v-if="loading">알림을 불러오는 중...</div>

      <div class="none_alarm" v-else-if="visibleList.length === 0">
        <img src="/assets/images/app/layout/alert/alarm.png" alt="alarm" />
        <p>최근 받은 알림이 없습니다.</p>
      </div>

      <ul class="alarm_list" v-else>
        <li
          v-for="item in visibleList"
          :key="item.idx"
          class="alarm_item"
          :class="{ unread: !item.isUserRead }"
          @click="openDetail(item)"
        >
          <div class="alarm_item_head">
            <span class="alarm_category" :class="item.category === 'INQUIRY' ? 'inquiry' : 'error'">
              {{ item.category === 'INQUIRY' ? '문의답변' : '오류신고답변' }}
            </span>
            <span class="alarm_date">{{ formatDate(item.replyDate || item.regDate) }}</span>
          </div>
          <p class="alarm_title">{{ item.title || item.errorType || '접수내역' }}</p>
          <p class="alarm_reply">{{ item.adminReply }}</p>
        </li>
      </ul>
    </div>

    <!-- 답변 상세 -->
    <div class="alarm_detail_overlay" v-if="detail" @click.self="closeDetail">
      <div class="alarm_detail_box">
        <div class="alarm_detail_header">
          <h4>{{ detail.category === 'INQUIRY' ? '관리자 문의 답변' : '오류신고 답변' }}</h4>
          <button class="alarm_detail_close" @click="closeDetail">&times;</button>
        </div>
        <div class="alarm_detail_body">
          <div class="detail_row">
            <span class="detail_label">접수일</span>
            <span class="detail_value">{{ formatDateTime(detail.regDate) }}</span>
          </div>
          <div class="detail_row">
            <span class="detail_label">유형</span>
            <span class="detail_value">{{ detail.errorType || '-' }}</span>
          </div>
          <div class="detail_row" v-if="detail.title">
            <span class="detail_label">제목</span>
            <span class="detail_value">{{ detail.title }}</span>
          </div>
          <div class="detail_row" v-if="detail.problemUrl">
            <span class="detail_label">페이지</span>
            <a class="detail_value link" :href="detail.problemUrl" target="_blank" rel="noopener noreferrer">
              {{ detail.problemUrl }}
            </a>
          </div>
          <div class="detail_row">
            <span class="detail_label">상태</span>
            <span class="detail_value">
              <span class="status_badge" :class="statusClass(detail.status)">{{ statusName(detail.status) }}</span>
            </span>
          </div>

          <div class="detail_block">
            <p class="detail_block_title">문의 내용</p>
            <p class="detail_block_text">{{ detail.message || '-' }}</p>
          </div>
          <div class="detail_block reply">
            <p class="detail_block_title">
              관리자 답변
              <span class="reply_date" v-if="detail.replyDate">{{ formatDateTime(detail.replyDate) }}</span>
            </p>
            <p class="detail_block_text">{{ detail.adminReply || '아직 답변이 등록되지 않았습니다.' }}</p>
          </div>
        </div>
        <div class="alarm_detail_footer">
          <button class="detail_more" @click="goInquiryPage">전체 문의내역 보기</button>
          <button class="detail_close_btn" @click="closeDetail">닫기</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import dayjs from 'dayjs';
import { errorReportAPI } from '@/api';

const STATUS_MAP = {
  OPEN: '접수',
  IN_PROGRESS: '처리중',
  RESOLVED: '완료'
};

export default {
  data() {
    return {
      alarmSwitch: localStorage.getItem('alarmSwitch') !== 'false',
      loading: false,
      marking: false,
      hideRead: false,
      alarmList: [],
      unreadCount: 0,
      detail: null
    };
  },
  created() {
    this.loadAlarm();
  },
  computed: {
    readCount() {
      return this.alarmList.filter(item => item.isUserRead).length;
    },
    visibleList() {
      if (!this.hideRead) return this.alarmList;
      return this.alarmList.filter(item => !item.isUserRead);
    }
  },
  methods: {
    async loadAlarm() {
      this.loading = true;
      try {
        // 관리자 답변이 등록된 건만 알림으로 노출한다.
        const res = await errorReportAPI.myList({ onlyReplied: true, page: 1, limit: 50 });
        if (res?.status === 'Success' && res.data) {
          this.alarmList = res.data.items ?? [];
          this.unreadCount = res.data.unreadCount ?? 0;
        } else {
          this.alarmList = [];
          this.unreadCount = 0;
        }
      } catch (error) {
        console.error('알림 조회 실패:', error);
        this.alarmList = [];
        this.unreadCount = 0;
      } finally {
        this.loading = false;
      }
    },
    async openDetail(item) {
      this.detail = item;
      if (!item.isUserRead) await this.markRead([item.idx]);
    },
    closeDetail() {
      this.detail = null;
    },
    async markRead(idxList) {
      try {
        const res = await errorReportAPI.markRead({ idxList, allRead: false });
        if (res?.status === 'Success') {
          this.alarmList = this.alarmList.map(item =>
            idxList.includes(item.idx) ? { ...item, isUserRead: true } : item
          );
          this.unreadCount = Math.max(0, this.unreadCount - idxList.length);
          this.$emit('updateUnread', this.unreadCount);
        }
      } catch (error) {
        console.error('알림 읽음 처리 실패:', error);
      }
    },
    async markAllRead() {
      this.marking = true;
      try {
        const res = await errorReportAPI.markRead({ allRead: true });
        if (res?.status === 'Success') {
          this.alarmList = this.alarmList.map(item => ({ ...item, isUserRead: true }));
          this.unreadCount = 0;
          this.$emit('updateUnread', 0);
        }
      } catch (error) {
        console.error('알림 전체 읽음 처리 실패:', error);
      } finally {
        this.marking = false;
      }
    },
    goInquiryPage() {
      this.closeDetail();
      this.$emit('closeAlert');
      if (this.$route.path !== '/app/preferences' || this.$route.query.tab !== 'AdminInquiry') {
        this.$router.push({ path: '/app/preferences', query: { tab: 'AdminInquiry' } }).catch(() => {});
      }
    },
    statusName(status) {
      return STATUS_MAP[status] || status || '-';
    },
    statusClass(status) {
      if (status === 'RESOLVED') return 'done';
      if (status === 'IN_PROGRESS') return 'progress';
      return 'open';
    },
    formatDate(date) {
      if (!date) return '-';
      return dayjs(date).isValid() ? dayjs(date).format('YYYY-MM-DD') : date;
    },
    formatDateTime(date) {
      if (!date) return '-';
      return dayjs(date).isValid() ? dayjs(date).format('YYYY-MM-DD HH:mm') : date;
    }
  },
  watch: {
    alarmSwitch(value) {
      localStorage.setItem('alarmSwitch', value ? 'true' : 'false');
    }
  }
};
</script>

<style lang="scss">
@import '@/assets/style/app/layout/alarm.scss';
</style>
